import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Send, CheckCircle, User, MessageSquare, FileText } from 'lucide-react';
import axios from 'axios';
import Footer from '@/components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState({ title: 'Fale Conosco', content: '' });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/content/contact_info`);
      setContent(response.data);
    } catch (error) {
      console.error('Failed to fetch content');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API_URL}/api/contact/send`, form);
      setSent(true);
      toast.success('Mensagem enviada com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setForm({ name: '', email: '', subject: '', message: '' });
    setSent(false);
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex flex-col">
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            data-testid="contact-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">{content.title}</h1>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-6 py-8 w-full">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Info */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#002FA7]/10 p-3 rounded-full">
                <Mail className="h-6 w-6 text-[#002FA7]" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Entre em Contato</h2>
                <p className="text-sm text-gray-500">Estamos aqui para ajudar</p>
              </div>
            </div>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin h-8 w-8 border-4 border-[#002FA7] border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : (
              <div className="prose max-w-none">
                {content.content.split('\n').map((paragraph, index) => (
                  <p key={index} className="mb-4 text-[#4B4C52] whitespace-pre-wrap">{paragraph}</p>
                ))}
              </div>
            )}

            <div className="mt-6 pt-6 border-t border-gray-100">
              <h3 className="font-semibold text-gray-700 mb-3">Canais de Atendimento</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-[#002FA7]" />
                  quero.publicar@queropublicar.org
                </p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            {sent ? (
              <div className="text-center py-12">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Mensagem Enviada!</h3>
                <p className="text-gray-600 mb-6">
                  Obrigado pelo contato. Responderemos em breve no email informado.
                </p>
                <Button
                  onClick={resetForm}
                  variant="outline"
                  data-testid="send-another-btn"
                >
                  Enviar outra mensagem
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-[#002FA7]/10 p-3 rounded-full">
                    <MessageSquare className="h-6 w-6 text-[#002FA7]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Envie sua Mensagem</h2>
                    <p className="text-sm text-gray-500">Responderemos em até 48 horas</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      Nome completo
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Seu nome"
                      className="mt-1"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="seu@email.com"
                      className="mt-1"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject" className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-gray-400" />
                      Assunto
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      placeholder="Sobre o que você quer falar?"
                      className="mt-1"
                      data-testid="contact-subject-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-gray-400" />
                      Mensagem
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Escreva sua mensagem aqui..."
                      rows={5}
                      className="mt-1 resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-[#002FA7] hover:bg-[#001f75]"
                    data-testid="contact-submit-btn"
                  >
                    {sending ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Enviar Mensagem
                      </>
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
